import { useClient } from '@cortex/react-query';
import type { Feedback } from '@cortex/types';
import { fork } from '@cortex/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, type CSSProperties } from 'react';

import { JsonViewer } from '../../components/common/JsonViewer';
import { PageHeader } from '../../components/common/PageHeader';
import { Spinner } from '../../components/common/Spinner';
import { FeedbackStatusBadge, SentimentBadge } from '../../components/feedback/FeedbackBadges';
import { formatDateTime } from '../../lib/format';

// Read-only audit history (DEV-16837). Feedback mints its eval assets at
// submit time (DEV-16834), so there is nothing to promote or dismiss here —
// this page answers "what did operator judgments turn into": run → ground
// truth → task → suite lineage. PENDING rows are the parked cases (negative
// without a correction, or feedback on an eval run flagging stale GT).

export const Route = createFileRoute('/feedback/')({
  component: FeedbackAuditPage,
  validateSearch: (raw: Record<string, unknown>): { status?: string } => ({
    status: typeof raw.status === 'string' ? raw.status : undefined,
  }),
});

/** Feedback with nested run + minted-lineage context from the API. */
interface IFeedbackWithLineage extends Feedback {
  run?: {
    id: string;
    input: unknown;
    output: unknown;
    workflowId: string;
    workflow: { name: string };
  };
  groundTruth?: {
    id: string;
    tasks: {
      id: string;
      name: string;
      suiteTasks: { suite: { id: string; name: string; kind: string } }[];
    }[];
  } | null;
}

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

const stack4: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' };
const stack3: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' };
const row2: CSSProperties = { display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' };
const filterBar: CSSProperties = {
  display: 'flex',
  gap: 'var(--sp-3)',
  alignItems: 'center',
  flexWrap: 'wrap',
};

function FeedbackAuditPage() {
  const search = Route.useSearch();
  const nav = Route.useNavigate();
  const client = useClient();

  const statusFilter = (search.status as string) ?? '';

  const setFilter = (updates: { status?: string }) => {
    nav({ search: { ...search, ...updates } }).catch(console.error);
  };

  const feedbackQuery = useQuery({
    queryKey: ['feedback', statusFilter],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('limit', '100');
      if (statusFilter) params.set('status', statusFilter);
      return client.http.get(`v1/feedback?${params}`).json<IListEnvelope<IFeedbackWithLineage>>();
    },
  });

  const [expandedId, setExpandedId] = useState<string | null>(null);

  // The one mutation the audit page keeps: dismissing a PARKED row that
  // turned out to be noise. Everything minted stays read-only history.
  const qc = useQueryClient();
  const dismiss = useMutation({
    mutationFn: (id: string) =>
      client.http.post(`v1/feedback/${id}/dismiss`, { json: {} }).json<unknown>(),
    onSuccess: () => fork(qc.invalidateQueries({ queryKey: ['feedback'] })),
  });

  const items = feedbackQuery.data?.items ?? [];

  return (
    <div style={stack4}>
      <PageHeader title="Feedback History" />
      <p className="t-secondary" style={{ margin: 0, fontSize: '0.85em' }}>
        Feedback mints its eval task at submit time — this is the audit trail. Pending rows are
        parked judgments: negative feedback awaiting a correction, or eval-run feedback flagging
        stale ground truth.
      </p>

      <div style={filterBar}>
        <label style={row2}>
          <span className="t-mono-label">STATUS</span>
          <select
            className="input"
            value={statusFilter}
            onChange={(e) => setFilter({ status: e.target.value })}
          >
            <option value="">All</option>
            <option value="PROMOTED">Minted</option>
            <option value="PENDING">Parked</option>
            <option value="DISMISSED">Dismissed</option>
          </select>
        </label>
      </div>

      {feedbackQuery.isLoading ? <Spinner /> : null}
      {feedbackQuery.error ? (
        <div className="alert alert--danger">
          Failed to load: {(feedbackQuery.error as Error).message}
        </div>
      ) : null}

      {!feedbackQuery.isLoading && items.length === 0 ? (
        <p className="t-secondary">No feedback entries match the current filters.</p>
      ) : null}

      {items.map((fb) => (
        <FeedbackCard
          key={fb.id}
          fb={fb}
          expanded={expandedId === fb.id}
          onToggleExpand={() => setExpandedId(expandedId === fb.id ? null : fb.id)}
          onDismiss={fb.status === 'PENDING' ? () => dismiss.mutate(fb.id) : undefined}
        />
      ))}
    </div>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────

interface ICardProps {
  fb: IFeedbackWithLineage;
  expanded: boolean;
  onToggleExpand: () => void;
  /** Present only for PARKED rows — dismissing noise is the audit page's one action. */
  onDismiss?: () => void;
}

function FeedbackCard({ fb, expanded, onToggleExpand, onDismiss }: ICardProps) {
  const workflowName = fb.run?.workflow?.name ?? 'Unknown workflow';

  return (
    <div className="card">
      <div className="card-body" style={stack3}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={row2}>
            <SentimentBadge sentiment={fb.sentiment} />
            <span style={{ fontWeight: 600 }}>{workflowName}</span>
            <span className="t-secondary" style={{ fontSize: '0.8em' }}>
              {formatDateTime(fb.createdAt)}
            </span>
          </div>
          <div style={row2}>
            <FeedbackStatusBadge
              status={fb.status}
              hasLineage={(fb.groundTruth?.tasks.length ?? 0) > 0}
            />
            <button type="button" className="btn btn--sm btn--ghost" onClick={onToggleExpand}>
              {expanded ? 'Collapse' : 'Details'}
            </button>
          </div>
        </div>

        {/* Comment */}
        {fb.comment ? (
          <p className="t-secondary" style={{ margin: 0, fontStyle: 'italic' }}>
            &ldquo;{fb.comment}&rdquo;
          </p>
        ) : null}

        {/* Lineage: run → GT → task → suite */}
        <LineageLine fb={fb} onDismiss={onDismiss} />

        {/* Expanded: corrected output, workflow output, run input */}
        {expanded ? (
          <div
            style={{
              ...stack3,
              borderTop: '1px solid var(--border-secondary)',
              paddingTop: 'var(--sp-3)',
            }}
          >
            {fb.correctedOutput ? (
              <div>
                <span className="t-mono-label">CORRECTED OUTPUT (operator&rsquo;s answer)</span>
                <JsonViewer value={fb.correctedOutput} />
              </div>
            ) : null}

            {fb.run?.output ? (
              <div>
                <span className="t-mono-label">WORKFLOW OUTPUT (what the system produced)</span>
                <JsonViewer value={fb.run.output} />
              </div>
            ) : null}

            {fb.run?.input ? (
              <div>
                <span className="t-mono-label">RUN INPUT</span>
                <JsonViewer value={fb.run.input} />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** One line summarizing what this feedback became. */
function LineageLine({ fb, onDismiss }: { fb: IFeedbackWithLineage; onDismiss?: () => void }) {
  const tasks = fb.groundTruth?.tasks ?? [];
  return (
    <div style={{ ...row2, flexWrap: 'wrap', fontSize: '0.8em' }}>
      {fb.run ? (
        <Link to="/triage/runs/$runId" params={{ runId: fb.run.id }} className="t-secondary">
          Run {fb.run.id.slice(0, 12)}…
        </Link>
      ) : null}
      {fb.groundTruthId ? (
        <>
          <span className="t-tertiary">→</span>
          <span className="t-secondary">GT {fb.groundTruthId.slice(0, 12)}…</span>
        </>
      ) : null}
      {tasks.map((t) => (
        <span key={t.id} style={row2}>
          <span className="t-tertiary">→</span>
          <span className="t-secondary">{t.name}</span>
          {t.suiteTasks.map((st) => (
            <span key={st.suite.id} className="t-mono-label" style={{ fontSize: '0.9em' }}>
              [{st.suite.kind === 'REGRESSION' ? 'REG' : 'CAP'}] {st.suite.name}
            </span>
          ))}
        </span>
      ))}
      {fb.status === 'PENDING' ? (
        <span className="t-secondary" style={{ ...row2, fontStyle: 'italic' }}>
          {fb.sentiment === 'NEGATIVE' && !fb.correctedOutput
            ? 'Parked — open the run to supply the correction (it mints on save)'
            : 'Parked — no eval assets minted'}
          {onDismiss ? (
            <button type="button" className="btn btn--sm btn--ghost" onClick={onDismiss}>
              Dismiss
            </button>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}
